import { Divider, Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { type ReactNode } from "react";

import { OnboardingTitle } from "../../SharedTitle";
import { ONBOARDING_STAGES } from "../../util/constants";
import { type OnboardingStepProps } from "../OnboardingStepWrapper";
import { type AgreementsToDisplay } from "../utils/getAgreementsToDisplay";
import { AgreementItem } from "./AgreementItem";

export interface AgreementExplanationStepContentProps extends OnboardingStepProps {
  agreementsToDisplay: AgreementsToDisplay;
  isICAgreementSigned: boolean;
  isJobSearchAgreementSigned: boolean;
  onNavigateToICAgreement: () => void;
  onNavigateToJobSearchAgreement: () => void;
}

function getTitle(agreementsToDisplay: AgreementsToDisplay): string {
  const { showIcAgreement, showJobAgreement } = agreementsToDisplay;

  if (showIcAgreement && showJobAgreement) {
    return "Two agreements to review";
  }

  return "One agreement to review";
}

function getDescription(agreementsToDisplay: AgreementsToDisplay): string {
  const { showIcAgreement, showJobAgreement } = agreementsToDisplay;

  if (showIcAgreement && showJobAgreement) {
    return "To give you the most flexibility in using Clipboard, we ask everyone to sign two separate agreements.";
  }

  return "To continue using Clipboard, please review and sign the following agreement.";
}

interface FooterButtonsParams {
  showIcAgreement: boolean;
  showJobAgreement: boolean;
  isICAgreementSigned: boolean;
  isJobSearchAgreementSigned: boolean;
  isJobAgreementOptional: boolean;
  onNavigateToICAgreement: () => void;
  onNavigateToJobSearchAgreement: () => void;
  onContinue: () => void;
}

function getFooterButtons(params: FooterButtonsParams): ReactNode {
  const {
    showIcAgreement,
    showJobAgreement,
    isICAgreementSigned,
    isJobSearchAgreementSigned,
    isJobAgreementOptional,
    onNavigateToICAgreement,
    onNavigateToJobSearchAgreement,
    onContinue,
  } = params;

  const hasBothAgreements = showIcAgreement && showJobAgreement;
  const reviewButtonText = hasBothAgreements ? "Review both agreements" : "Review agreement";

  // Case 1: IC agreement needs signing
  if (showIcAgreement && !isICAgreementSigned) {
    return (
      <Button fullWidth variant="brand" size="large" onClick={onNavigateToICAgreement}>
        {reviewButtonText}
      </Button>
    );
  }

  // Case 2: Job agreement needs action (IC either signed or not shown)
  if (showJobAgreement && !isJobSearchAgreementSigned) {
    return (
      <>
        <Button fullWidth variant="brand" size="large" onClick={onNavigateToJobSearchAgreement}>
          {reviewButtonText}
        </Button>
        {isJobAgreementOptional && (
          <TextButton underline onClick={onContinue}>
            Skip
          </TextButton>
        )}
      </>
    );
  }

  // Case 3: All agreements satisfied - show Continue
  return (
    <Button fullWidth variant="brand" size="large" onClick={onContinue}>
      Continue
    </Button>
  );
}

export function AgreementExplanationStepContent(props: AgreementExplanationStepContentProps) {
  const {
    onNext,
    agreementsToDisplay,
    isICAgreementSigned,
    isJobSearchAgreementSigned,
    onNavigateToICAgreement,
    onNavigateToJobSearchAgreement,
  } = props;

  const { showIcAgreement, showJobAgreement, isJobAgreementOptional } = agreementsToDisplay;

  useLogEffect(APP_V2_USER_EVENTS.ONBOARDING_AGREEMENT_EXPLANATION_PAGE_VIEWED);

  const showDivider = showIcAgreement && showJobAgreement;

  return (
    <>
      <PageContent>
        <OnboardingTitle text={getTitle(agreementsToDisplay)} />

        <Stack
          direction="column"
          sx={(theme) => ({
            marginTop: theme.spacing(6),
            flex: 1,
            maxHeight: 600,
          })}
        >
          <Text variant="body1">{getDescription(agreementsToDisplay)}</Text>

          <Stack
            direction="column"
            justifyContent="space-around"
            sx={{
              gap: 7,
              paddingTop: 10,
            }}
          >
            {showIcAgreement && (
              <AgreementItem
                title="Independent Contractor Agreement"
                description="For picking up individual per-diem shifts as an Independent Contractor with Clipboard"
                isSigned={isICAgreementSigned}
              />
            )}

            {showDivider && (
              <Divider
                sx={(theme) => ({
                  marginY: theme.spacing(4),
                  "&::before, &::after": {
                    borderTopWidth: 2,
                  },
                })}
              >
                <Text variant="h6" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
                  and
                </Text>
              </Divider>
            )}

            {showJobAgreement && (
              <AgreementItem
                title="Job Search User Agreement"
                description="For applying to part time, full time, and PRN positions at workplaces to be their employee"
                isSigned={isJobSearchAgreementSigned}
                sx={{ paddingBottom: 12 }}
              />
            )}
          </Stack>
        </Stack>
      </PageContent>

      <PageFooterWrapper>
        <Stack direction="column" spacing={3} alignItems="center" sx={{ width: "100%" }}>
          {getFooterButtons({
            showIcAgreement,
            showJobAgreement,
            isICAgreementSigned,
            isJobSearchAgreementSigned,
            isJobAgreementOptional,
            onNavigateToICAgreement,
            onNavigateToJobSearchAgreement,
            onContinue: async () => {
              logEvent(APP_V2_USER_EVENTS.ONBOARDING_STEP_COMPLETED, {
                stepCompleted: ONBOARDING_STAGES.AGREEMENT_EXPLANATION,
              });
              await onNext();
            },
          })}
        </Stack>
      </PageFooterWrapper>
    </>
  );
}
